import React, { useState, useContext, useEffect } from 'react';
import { useAuth0, useAuth0Data } from 'neon-auth0';
import { ResearchRole } from '../../shared/global-enums';
import jwt from 'jwt-decode';
import { translateRoles, getRolesFromScope, trialScope, nonTrialScopes } from '../../utils/auth.utils';
import { config } from '../../config';
import { CurrentUserDetails } from '../../shared/types';

export interface Auth0Token {
	sub: string;
	scope: string;
}

export enum TrialState {
	NoTrial,
	TrialContinues,
	TrialExpired
}

export interface UserContextData {
	userDetails?: CurrentUserDetails;
	hasScope(role: ResearchRole): boolean;
	hasIntelligenceScope(): boolean;
	updateTrialState(state: TrialState): void;
	trialState: TrialState;
}

const UserContext = React.createContext<UserContextData>({
	userDetails: undefined,
	hasScope: (role: ResearchRole) => { return false; },
	hasIntelligenceScope: () => { return false; },
	updateTrialState: (state: TrialState) => { },
	trialState: TrialState.NoTrial
});

export const useUser = (): UserContextData => useContext(UserContext);

export interface UserProviderParams {
	children: JSX.Element | JSX.Element[];
}

export const UserProvider = ({ children }: UserProviderParams): JSX.Element => {
	const { user } = useAuth0();
	const { accessToken } = useAuth0Data();
	const decodedToken: Auth0Token = jwt(accessToken);
	const userDetails: CurrentUserDetails = getUserDetails(user, decodedToken, accessToken);

	const [trialState, setTrialState] = useState(TrialState.NoTrial);

	useEffect(() => {

		const splitted = decodedToken.scope.split(' ');
		if (trialState !== TrialState.TrialExpired
			&& decodedToken.scope.includes(trialScope)
			&& !nonTrialScopes.some(x =>splitted.includes(x))
		) {
			setTrialState(TrialState.TrialContinues);
		}
	}, [decodedToken, trialState]);

	function updateTrialState(state: TrialState): void {
		if (trialState !== TrialState.TrialExpired) {
			setTrialState(state);
		}
	}


	const hasScope = (role: ResearchRole): boolean => {
		if (!decodedToken) return false;

		const roles = getRolesFromScope(decodedToken && decodedToken.scope);
		return roles && roles.some(p => p === role);
	}

	const hasIntelligenceScope = (): boolean => {
		if (!decodedToken) return false;

		return decodedToken.scope.split(' ').indexOf('intelligence') > -1;
	}

	return (
		<UserContext.Provider value={{ userDetails, trialState, hasScope, hasIntelligenceScope, updateTrialState }}>
			{children}
		</UserContext.Provider>
	);
};

function getUserDetails(user: any, decodedToken: Auth0Token, accessToken: string): CurrentUserDetails {
	const role = translateRoles(decodedToken && decodedToken.scope);

	return {
		name: (user?.given_name && user?.family_name && (user.given_name + ' ' + user.family_name)) ||
			(user?.name) ||
			(user?.email) ||
			'',
		role: role,
		id: jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'],
		isInternal: decodedToken.sub.startsWith('adfs|MarexADFS|'),
		email: user?.email
	};
}
